// UPDATE-shape extractor — pulls updateTarget / updateAssignments / FROM/JOIN
// (PG, MSSQL) / WHERE / RETURNING.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — DML.

import { scanTopLevelKeywords } from './scanner';
import type { SelectExtractCtx } from './extractQueryStructure.select';
import { parseFromAndJoins } from './extractQueryStructure.from';
import { parseBooleanFragments } from './extractQueryStructure.predicates';
import { parseSelectItems } from './extractQueryStructure.selectList';
import {
  parseAssignmentsRange, pickFirstStart, readDmlTarget,
  type MutableQueryStructure,
} from './extractQueryStructure.dml.shared';

// eslint-disable-next-line complexity
export function extractUpdateShape(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): MutableQueryStructure {
  const { source, options } = ctx;
  const kws = scanTopLevelKeywords(source, start, end, options);
  const updateIdx = kws.findIndex((k) => k.upper === 'UPDATE');
  if (updateIdx < 0) return {};
  const target = readDmlTarget(ctx, kws[updateIdx].end, end);
  if (!target) return {};

  const setIdx = kws.findIndex((k, i) => i > updateIdx && k.upper === 'SET');
  if (setIdx < 0) return { updateTarget: target.item };

  const fromIdx = kws.findIndex((k, i) => i > setIdx && k.upper === 'FROM');
  const whereIdx = kws.findIndex((k, i) => i > setIdx && k.upper === 'WHERE');
  const returningIdx = kws.findIndex((k, i) => i > setIdx && k.upper === 'RETURNING');

  const setEnd = pickFirstStart(kws, [fromIdx, whereIdx, returningIdx], end);
  const assignments = parseAssignmentsRange(source, options, ctx.lineIdx, kws[setIdx].end, setEnd);

  const out: MutableQueryStructure = { updateTarget: target.item };
  if (assignments.length > 0) out.updateAssignments = assignments;

  if (fromIdx >= 0) {
    const fromEnd = pickFirstStart(kws, [whereIdx, returningIdx], end);
    const split = parseFromAndJoins(ctx, kws[fromIdx].end, fromEnd);
    if (split.fromItems.length > 0) out.fromItems = split.fromItems;
    if (split.joins.length > 0) out.joins = split.joins;
  }
  if (whereIdx >= 0) {
    const whereEnd = pickFirstStart(kws, [returningIdx], end);
    const where = parseBooleanFragments(ctx, kws[whereIdx].end, whereEnd);
    if (where) out.where = where;
  }
  if (returningIdx >= 0) {
    const ret = parseSelectItems(ctx, kws[returningIdx].end, end);
    if (ret.length > 0) out.returning = ret;
  }
  return out;
}
